// Town kit: a triangle-soup builder whose every face samples one tile of the painted town
// atlas (public/assets/town/atlas.webp, generated by _src/gen_atlas.py), plus the two
// materials that read it (opaque atlas, alpha-cut atlas) and a matching shadow-depth
// material. Repeating tiles (clapboard, shingles, planks...) wrap inside their atlas cell in
// the shader, so a whole street of differently sized walls is still one draw call.
//
// Per-vertex attributes: position, normal (flat), uv = tile-local coords (metres / tile size
// for repeating tiles, 0..1 for decals), tile = atlas rect (u0, v0, du, dv), tint = rgb
// multiplier on the painted parts (atlas alpha) + a baked occlusion term in .a.

import * as THREE from 'three';
import { HAZE, withHaze } from '../materials';
import { TILES, TileName } from './town_atlas';
import { sharedTexture } from '../warm';

/** Physical size in metres of one repeat of each repeating tile. */
export const TILE_M: Partial<Record<TileName, number>> = {
  clap: 2, clap2: 2, batten: 2, shingle: 2, corrugated: 2, planks: 2,
  brick: 1, stone: 1.4, cobble: 3.6, trim: 1, rawwood: 1, metal: 1, plain: 1, enamel: 1,
};

export type RGB = readonly [number, number, number];
export const WHITE: RGB = [1, 1, 1];

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _n = new THREE.Vector3();

export class KitGeo {
  pos: number[] = [];
  nrm: number[] = [];
  uv: number[] = [];
  tile: number[] = [];
  tint: number[] = [];

  get triCount() { return this.pos.length / 9; }

  /**
   * One triangle; p = [x,y,z] x3 counter-clockwise from the front, t = [u,v] x3,
   * ao per corner (1 = open).
   */
  tri(p: number[][], t: number[][], name: TileName, col: RGB, ao: number[] = ONE3) {
    const r = TILES[name];
    _a.set(p[1][0] - p[0][0], p[1][1] - p[0][1], p[1][2] - p[0][2]);
    _b.set(p[2][0] - p[0][0], p[2][1] - p[0][1], p[2][2] - p[0][2]);
    _n.crossVectors(_a, _b);
    const l = _n.length();
    if (l < 1e-9) return;
    _n.multiplyScalar(1 / l);
    for (let i = 0; i < 3; i++) {
      this.pos.push(p[i][0], p[i][1], p[i][2]);
      this.nrm.push(_n.x, _n.y, _n.z);
      this.uv.push(t[i][0], t[i][1]);
      // w carries the repeat mode: < 0 clamp, > 10 repeat u only, else repeat both (tileUv)
      this.tile.push(r[0], r[1], r[2], r[4] === 0 ? -r[3] : r[4] === 2 ? r[3] + 10 : r[3]);
      this.tint.push(col[0], col[1], col[2], ao[i]);
    }
  }

  /** Quad a,b,c,d CCW from the front with explicit uvs. */
  quad(a: number[], b: number[], c: number[], d: number[], ua: number[], ub: number[], uc: number[], ud: number[], name: TileName, col: RGB, ao?: number[]) {
    const aa = ao ?? ONE4;
    this.tri([a, b, c], [ua, ub, uc], name, col, [aa[0], aa[1], aa[2]]);
    this.tri([a, c, d], [ua, uc, ud], name, col, [aa[0], aa[2], aa[3]]);
  }

  /** Quad with the whole decal tile stretched over it (a = bottom-left as seen from the front). */
  decal(a: number[], b: number[], c: number[], d: number[], name: TileName, col: RGB = WHITE, ao?: number[], u0 = 0, u1 = 1, v0 = 0, v1 = 1) {
    this.quad(a, b, c, d, [u0, v0], [u1, v0], [u1, v1], [u0, v1], name, col, ao);
  }

  append(o: KitGeo) {
    for (const k of ['pos', 'nrm', 'uv', 'tile', 'tint'] as const) {
      const src = o[k], dst = this[k];
      for (let i = 0; i < src.length; i++) dst.push(src[i]);
    }
  }

  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute('tile', new THREE.Float32BufferAttribute(this.tile, 4));
    g.setAttribute('tint', new THREE.Float32BufferAttribute(this.tint, 4));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}
const ONE3 = [1, 1, 1], ONE4 = [1, 1, 1, 1];

// ------------------------------------------------------------------ shader
const TILES_ATLAS_PX = '2048.0';
const TILE_VERT_DECL = /* glsl */ `
attribute vec4 tile;
attribute vec4 tint;
varying vec2 vLocal;
varying vec4 vTile;
varying vec4 vTint;
`;
const TILE_VERT = /* glsl */ `
vLocal = uv; vTile = tile; vTint = tint;
`;

// ---- night: lit windows, glowing lanterns and the warm pools they throw on the street
const near = (n: TileName) => `( abs( tile.x - ${TILES[n][0].toFixed(6)} ) + abs( tile.y - ${TILES[n][1].toFixed(6)} ) < 1e-4 )`;
const GLOW_VERT_DECL = 'varying float vGlow;\n';
// 1 = window glass (lamplit rooms), 2 = lantern, 3 = television
const GLOW_VERT = /* glsl */ `
vGlow = ( ${(['win0', 'win1', 'win2', 'win3', 'win4', 'winRound'] as TileName[]).map(near).join(' || ')} ) ? 1.0
  : ${near('lantern')} ? 2.0 : ${near('tv')} ? 3.0
  : ( ${(['shop0', 'shop1', 'shop2'] as TileName[]).map(near).join(' || ')} ) ? 4.0 : 0.0;
`;
const GLOW_FRAG_DECL = /* glsl */ `
varying float vGlow;
uniform sampler2D tLamp;
uniform vec4 uLampRect; // x0, z0, 1/width, 1/depth
uniform float uLampY0;
float lampHash( vec3 p ) { return fract( sin( dot( p, vec3( 12.9898, 78.233, 37.719 ) ) ) * 43758.5453 ); }
float lampNoise( vec3 p ) {
  vec3 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( mix( lampHash( i ), lampHash( i + vec3( 1, 0, 0 ) ), f.x ), mix( lampHash( i + vec3( 0, 1, 0 ) ), lampHash( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
              mix( mix( lampHash( i + vec3( 0, 0, 1 ) ), lampHash( i + vec3( 1, 0, 1 ) ), f.x ), mix( lampHash( i + vec3( 0, 1, 1 ) ), lampHash( i + 1.0 ), f.x ), f.y ), f.z );
}
`;
const GLOW_FRAG = /* glsl */ `
if ( uNight > 0.001 ) {
  vec3 wp = vHazeWorld;
  // the moon is a weak key in a street this narrow: the town sinks so its own lights carry it
  outgoingLight *= mix( vec3( 1.0 ), vec3( 0.33, 0.43, 0.52 ), uNight );
  // lantern pools: a baked map of every lantern on the town's footprint (r: light, g: lamp height)
  vec2 luv = ( wp.xz - uLampRect.xy ) * uLampRect.zw;
  vec2 lm = texture2D( tLamp, luv ).rg;
  float ly = uLampY0 + lm.g * 16.0;
  float pool = lm.r * ( 1.0 - smoothstep( 0.3, 1.8, wp.y - ly ) ) * ( 1.0 - smoothstep( 6.0, 11.0, ly - wp.y ) );
  outgoingLight += diffuseColor.rgb * vec3( 1.0, 0.58, 0.28 ) * pool * ( 0.6 + 0.4 * pool ) * 2.1 * uNight;
  float lum = dot( texel.rgb, vec3( 0.333 ) );
  if ( vGlow > 0.5 && ( vGlow < 1.5 || vGlow > 3.5 ) ) {
    // window glass: lamplight in the room behind, brighter low (the lamp) and across the panes;
    // about a third of the town has gone to bed (shopfronts stay lit: their goods glow too)
    float glass = vGlow > 3.5 ? 0.1 + 0.9 * ( 1.0 - smoothstep( 0.18, 0.5, lum ) ) : 1.0 - smoothstep( 0.1, 0.34, lum );
    float n = lampNoise( wp * vec3( 0.42, 0.6, 0.42 ) );
    float on = vGlow > 3.5 ? 0.62 : smoothstep( 0.36, 0.5, n );
    vec3 room = mix( vec3( 1.0, 0.5, 0.2 ), vec3( 1.0, 0.74, 0.42 ), n );
    outgoingLight = mix( outgoingLight, room * ( 0.9 + 0.9 * ( 1.0 - vLocal.y ) ) * ( 0.75 + 0.5 * texel.r ), glass * on * uNight );
  } else if ( vGlow > 1.5 && vGlow < 2.5 ) {
    // the lantern itself: hot core over the bloom's white point
    outgoingLight = mix( outgoingLight, ( texel.rgb * 0.6 + vec3( 1.0, 0.62, 0.3 ) ) * ( 1.8 + 4.0 * smoothstep( 0.25, 0.7, lum ) ), uNight );
  } else if ( vGlow > 2.5 && vGlow < 3.5 ) {
    float scr = 1.0 - smoothstep( 0.12, 0.4, lum );
    outgoingLight = mix( outgoingLight, vec3( 0.42, 0.68, 1.0 ) * 1.4, scr * uNight * 0.85 );
  }
}
`;
const TILE_FRAG_DECL = /* glsl */ `
uniform sampler2D tAtlas;
uniform sampler2D tAtlasN;
uniform float uNormalScale;
varying vec2 vLocal;
varying vec4 vTile;
varying vec4 vTint;
// w > 10: repeat u only; w < 0: clamp; else repeat both
vec2 tileUv( out vec2 gx, out vec2 gy ) {
  float w = vTile.w;
  vec2 size = vec2( vTile.z, w > 10.0 ? w - 10.0 : abs( w ) );
  vec2 l = vLocal;
  vec2 f;
  if ( w < 0.0 ) f = clamp( l, 0.004, 0.996 );
  else if ( w > 10.0 ) f = vec2( fract( l.x ), clamp( l.y, 0.004, 0.996 ) );
  else f = fract( l );
  // gradients of the continuous coordinate: no seams at the wrap, and cap the mip so
  // neighbouring cells never bleed in
  gx = dFdx( l ) * size; gy = dFdy( l ) * size;
  // keep grazing surfaces (the street, the boardwalks) crisp: limit the footprint's anisotropy so
  // the mip follows the minor axis even where the sampler's own anisotropic filtering is weak,
  // then cap the mip so neighbouring cells never bleed in
  float lx = length( gx ), ly = length( gy );
  float lmin = max( min( lx, ly ), 1e-7 );
  float amax = 4.0;
  if ( lx > amax * lmin ) gx *= amax * lmin / lx;
  if ( ly > amax * lmin ) gy *= amax * lmin / ly;
  float m = max( length( gx ), length( gy ) ) * ${TILES_ATLAS_PX};
  float cap = 8.0;
  if ( m > cap ) { gx *= cap / m; gy *= cap / m; }
  return vTile.xy + f * size;
}
`;



function patchTile(shader: THREE.WebGLProgramParametersWithUniforms, opts: { normal: boolean; cutout: boolean; paintMask: boolean }) {
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\n' + TILE_VERT_DECL + GLOW_VERT_DECL)
    .replace('#include <uv_vertex>', '#include <uv_vertex>\n' + TILE_VERT + GLOW_VERT);
  let frag = shader.fragmentShader.replace('#include <common>', '#include <common>\n' + TILE_FRAG_DECL + GLOW_FRAG_DECL);
  frag = frag.replace('#include <map_fragment>', /* glsl */ `
    vec2 tgx, tgy;
    vec2 tuv = tileUv( tgx, tgy );
    vec4 texel = textureGrad( tAtlas, tuv, tgx, tgy );
    ${opts.cutout ? 'if ( texel.a < 0.5 ) discard;\n    diffuseColor.rgb *= texel.rgb * vTint.rgb;' : ''}
    ${opts.paintMask ? 'diffuseColor.rgb *= mix( texel.rgb, texel.rgb * vTint.rgb, clamp( texel.a * 2.0 - 1.0, 0.0, 1.0 ) );' : ''}
    ${opts.cutout ? '' : '{ float gr = 1.0 - smoothstep( 0.5, 0.92, vTint.a ); diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * vec3( 0.8, 0.7, 0.55 ), gr * ( 0.45 + 0.55 * texel.g ) ); }'}
    // occlusion bites hard: the ref's darks live under eaves, awnings and porches
    diffuseColor.rgb *= vTint.a * vTint.a;
  `);
  // shade side reads cool teal like the reference (sky fill), sunlit side stays warm
  frag = frag.replace('#include <lights_fragment_end>', '#include <lights_fragment_end>\n  { float upN = clamp( dot( normal, normalize( ( viewMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz ) ), 0.0, 1.0 );\n    reflectedLight.indirectDiffuse *= mix( vec3( 2.0, 2.2, 2.5 ), vec3( 1.8, 2.1, 2.95 ), upN );\n    // painted darks (glass interiors, lap lines, ink) stay dark: the big sky fill would grey them\n    reflectedLight.indirectDiffuse *= mix( 0.22, 1.0, smoothstep( 0.02, 0.16, dot( texel.rgb, vec3( 0.333 ) ) ) ); }\n  { float upD = clamp( dot( normal, normalize( ( viewMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz ) ), 0.0, 1.0 );\n    reflectedLight.directDiffuse *= vec3( 0.92, 1.0, 1.45 ) * mix( 1.15, 0.85, upD );\n    // moonlight between the buildings stays a soft slab, not a cold white hole\n    reflectedLight.directDiffuse *= 1.0 - 0.55 * uNight; }');
  // the shaded side keeps its paint: push saturation back up where the light is low, so the teal
  // in shadow reads teal (the ref) and not grey
  frag = frag.replace('#include <opaque_fragment>', /* glsl */ `
    {
      float lo = dot( outgoingLight, vec3( 0.299, 0.587, 0.114 ) );
      // walls only: the street and roofs stay as painted
      float up = abs( dot( normalize( normal ), normalize( ( viewMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz ) ) );
      // dusty, not candy: the whole town sits ~22% under the painted saturation
      float k = 0.79 + ( 1.0 - smoothstep( 0.4, 0.8, up ) ) * 0.06 * ( 1.0 - smoothstep( 0.05, 0.35, lo ) );
      outgoingLight = max( mix( vec3( lo ), outgoingLight, k ), 0.0 );
      // print ink: the low end sinks toward the darks the ref is full of
      outgoingLight *= mix( 0.72, 1.0, smoothstep( 0.02, 0.12, lo ) );
    }
    ${GLOW_FRAG}
    #include <opaque_fragment>`);
  if (opts.normal) {
    frag = frag.replace('#include <normal_fragment_maps>', /* glsl */ `
    {
      vec3 mapN = textureGrad( tAtlasN, tuv, tgx, tgy ).xyz * 2.0 - 1.0;
      mapN.xy *= uNormalScale;
      vec3 q0 = dFdx( - vViewPosition ), q1 = dFdy( - vViewPosition );
      vec2 st0 = dFdx( vLocal ), st1 = dFdy( vLocal );
      vec3 N = normal;
      vec3 q1perp = cross( q1, N ), q0perp = cross( N, q0 );
      vec3 T = q1perp * st0.x + q0perp * st1.x;
      vec3 B = q1perp * st0.y + q0perp * st1.y;
      float det = max( dot( T, T ), dot( B, B ) );
      float sc = ( det == 0.0 ) ? 0.0 : inversesqrt( det );
      normal = normalize( T * ( mapN.x * sc ) + B * ( mapN.y * sc ) + N * mapN.z );
    }
    `);
  }
  shader.fragmentShader = frag;
}

/** Soft three-step print ramp: the plank lips and shingle butts catch light without banding. */
let rampTex: THREE.DataTexture | null = null;
function townRamp() {
  if (rampTex) return rampTex;
  const N = 64, data = new Uint8Array(N * 4);
  for (let i = 0; i < N; i++) {
    const d = (i / (N - 1)) * 2 - 1; // dot(N, L)
    const s = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
    // faces turned from the sun keep a little of it (the print look), lit faces get all of it
    const v = Math.min(1, 0.32 + 0.3 * s(-0.08, 0.06, d) + 0.38 * s(0.22, 0.42, d));
    const c = Math.min(255, Math.round(v * 255)); // Uint8: never wrap (1.04 wrapped to black once)
    data.set([c, c, c, 255], i * 4);
  }
  rampTex = new THREE.DataTexture(data, N, 1, THREE.RGBAFormat);
  rampTex.minFilter = THREE.LinearFilter; rampTex.magFilter = THREE.LinearFilter; rampTex.generateMipmaps = false;
  rampTex.needsUpdate = true;
  return rampTex;
}

export interface AtlasTextures { map: THREE.Texture; normal: THREE.Texture; }

export function loadAtlas(quality: number, maxAniso = 8): AtlasTextures {
  const base = (import.meta as any).env?.BASE_URL ?? '/';
  // (shared across islands: loaded and uploaded once a session)
  const aniso = quality >= 2 ? maxAniso : quality >= 1 ? Math.min(4, maxAniso) : 1;
  const map = sharedTexture(base + 'assets/town/atlas.webp', `${aniso}`, (t) => {
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = aniso;
    t.generateMipmaps = true;
    t.minFilter = THREE.LinearMipmapLinearFilter;
  });
  const normal = sharedTexture(base + 'assets/town/atlas_n.webp', `${aniso}`, (t) => { t.colorSpace = THREE.NoColorSpace; t.anisotropy = aniso; });
  return { map, normal };
}

/** Opaque atlas material (walls, roofs, trim, decals). */
export function atlasMaterial(tex: AtlasTextures, quality: number, opts: { cutout?: boolean; lamps?: LampMap } = {}) {
  const cutout = !!opts.cutout;
  const useNormal = quality >= 1 && !cutout;
  const m = new THREE.MeshToonMaterial({ color: 0xffffff, gradientMap: townRamp(), side: cutout ? THREE.DoubleSide : THREE.FrontSide });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.tAtlas = { value: tex.map };
    shader.uniforms.tAtlasN = { value: tex.normal };
    shader.uniforms.uNormalScale = { value: 1.0 };
    const lm = opts.lamps ?? noLamps();
    shader.uniforms.tLamp = { value: lm.tex };
    shader.uniforms.uLampRect = { value: lm.rect };
    shader.uniforms.uLampY0 = { value: lm.y0 };
    patchTile(shader, { normal: useNormal, cutout, paintMask: !cutout });
  };
  // the street is close: keep the town's colour, let only the far end sink into the haze
  withHaze(m, 0.6);
  // withHaze sets a key shared by every toon material; ours compiles a different program
  m.customProgramCacheKey = () => `townAtlas_${cutout ? 'cut' : 'opq'}_${useNormal ? 'n' : ''}`;
  return m;
}

export interface LampMap { tex: THREE.DataTexture; rect: THREE.Vector4; y0: number; count: number; }

let emptyLamps: LampMap | null = null;
function noLamps(): LampMap {
  if (!emptyLamps) {
    const tex = new THREE.DataTexture(new Uint8Array(4), 1, 1, THREE.RGBAFormat);
    tex.needsUpdate = true;
    emptyLamps = { tex, rect: new THREE.Vector4(0, 0, 1, 1), y0: 0, count: 0 };
  }
  return emptyLamps;
}

/**
 * The night light of the town's lanterns, baked once into a small top-down map over the town's
 * footprint (r = warm light, g = lamp height): every lantern pools on the street and the walls
 * around it with no lights in the scene. Lanterns are found in the alpha-cut geometry (crossCards
 * writes each as 12 consecutive vertices on the lantern tile).
 */
export function lampMap(cut: KitGeo, solid?: KitGeo, px = 0.4, radius = 2.4): LampMap {
  const lamps: number[] = [];
  // runs of `per` consecutive vertices on one tile -> one light at their centre (top for lanterns),
  // pushed `out` metres along the face normal (shop glass spills into the street)
  const scan = (g: KitGeo, names: TileName[], per: number, out: number, gain: number) => {
    const t = g.tile, p = g.pos, nr = g.nrm, n = t.length / 4;
    const hit = (i: number) => names.some((nm) => Math.abs(t[i * 4] - TILES[nm][0]) + Math.abs(t[i * 4 + 1] - TILES[nm][1]) < 1e-4);
    for (let i = 0; i < n;) {
      if (!hit(i)) { i++; continue; }
      let x = 0, y = 0, z = 0, k = 0;
      for (; k < per && i + k < n && hit(i + k); k++) { x += p[(i + k) * 3]; y = per > 6 ? Math.max(y, p[(i + k) * 3 + 1]) : y + p[(i + k) * 3 + 1]; z += p[(i + k) * 3 + 2]; }
      lamps.push(x / k + nr[i * 3] * out, per > 6 ? y : y / k, z / k + nr[i * 3 + 2] * out, gain);
      i += k;
    }
  };
  scan(cut, ['lantern'], 12, 0, 1);
  if (solid) scan(solid, ['shop0', 'shop1', 'shop2'], 6, 1.2, 0.7);
  if (!lamps.length) return noLamps();
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, y0 = Infinity;
  for (let i = 0; i < lamps.length; i += 4) {
    x0 = Math.min(x0, lamps[i]); x1 = Math.max(x1, lamps[i]);
    z0 = Math.min(z0, lamps[i + 2]); z1 = Math.max(z1, lamps[i + 2]);
    y0 = Math.min(y0, lamps[i + 1]);
  }
  const m = radius * 5;
  x0 -= m; x1 += m; z0 -= m; z1 += m;
  const W = Math.min(512, Math.ceil((x1 - x0) / px)), H = Math.min(512, Math.ceil((z1 - z0) / px));
  const light = new Float32Array(W * H), hy = new Float32Array(W * H);
  for (let i = 0; i < lamps.length; i += 4) {
    const cx = (lamps[i] - x0) / px, cz = (lamps[i + 2] - z0) / px;
    // a lamp up a pole throws a wider pool than one by a door
    const rr = Math.min(radius * 2, radius + 0.45 * (lamps[i + 1] - y0)), r2 = rr * rr, reach = Math.ceil(rr * 2.5 / px);
    for (let v = Math.max(0, (cz | 0) - reach); v < Math.min(H, (cz | 0) + reach); v++) {
      for (let u = Math.max(0, (cx | 0) - reach); u < Math.min(W, (cx | 0) + reach); u++) {
        const dx = (u + 0.5 - cx) * px, dz = (v + 0.5 - cz) * px;
        const w = 0.85 * lamps[i + 3] * Math.exp(-(dx * dx + dz * dz) / r2);
        const j = v * W + u;
        // the nearest (strongest) lamp sets the height
        if (w > light[j] * 0.5) hy[j] = w > light[j] ? lamps[i + 1] : hy[j];
        light[j] += w;
      }
    }
  }
  const data = new Uint8Array(W * H * 4);
  for (let j = 0; j < W * H; j++) {
    data[j * 4] = Math.round(255 * Math.min(1, light[j]));
    data[j * 4 + 1] = Math.round(255 * Math.min(1, Math.max(0, (hy[j] - y0) / 16)));
    data[j * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat);
  tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return { tex, rect: new THREE.Vector4(x0, z0, 1 / (W * px), 1 / (H * px)), y0, count: lamps.length / 4 };
}

/** Shadow depth for the alpha-cut mesh: same tile lookup, discard the holes. */
export function atlasDepthMaterial(tex: AtlasTextures) {
  const m = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, side: THREE.DoubleSide });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.tAtlas = { value: tex.map };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + TILE_VERT_DECL)
      .replace('#include <uv_vertex>', '#include <uv_vertex>\n' + TILE_VERT);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform sampler2D tAtlas;\n' + TILE_FRAG_DECL.replace('uniform sampler2D tAtlas;', ''))
      .replace('#include <map_fragment>', /* glsl */ `
        vec2 tgx, tgy; vec2 tuv = tileUv( tgx, tgy );
        if ( textureGrad( tAtlas, tuv, tgx, tgy ).a < 0.5 ) discard;
      `);
  };
  m.customProgramCacheKey = () => 'townAtlasDepth';
  return m;
}

export { HAZE };
